import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@libs/axios/axios';
import { RETURN_TRIAL_PATH, returnTrialAttemptKey } from '@features/subscription/returnTrial';
import { intentFromSearch } from '@features/checkout/intent';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Loader from '@shared/components/Loader';
import { useIntl } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthContext } from './AuthContext';

const AuthGate = () => {
  const { isAuthenticated, ready, isDemo, user } = useAuthContext();
  const location = useLocation();
  const intl = useIntl();
  const { loginWithRedirect } = useAuth0();

  const checkTrial =
    ready &&
    isAuthenticated &&
    !isDemo &&
    !!user?.sub &&
    location.pathname !== RETURN_TRIAL_PATH &&
    new URLSearchParams(location.search).get('action') !== 'change' &&
    !sessionStorage.getItem(returnTrialAttemptKey(user.sub));
  const trial = useQuery({
    queryKey: ['returnTrialEligibility', user?.sub],
    queryFn: async () =>
      (await axiosInstance.get<{ eligible: boolean }>('/api/checkout/trial-eligibility')).data,
    enabled: checkTrial,
    retry: false,
    staleTime: 0,
  });

  if (
    location.pathname === '/hairdresser/subscription' &&
    intentFromSearch(location.search) &&
    !isAuthenticated &&
    !isDemo
  ) {
    return <Navigate to={`/checkout${location.search}`} replace />;
  }

  if (isDemo) {
    return <Outlet />;
  }

  if (!ready) {
    return (
      <Loader
        title={intl.formatMessage({
          id: 'authGate.loading',
          defaultMessage: 'Ověřuji přihlášení...',
        })}
      />
    );
  }

  if (!isAuthenticated) {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin + location.pathname + location.search,
      },
    });

    return null;
  }

  if (checkTrial && trial.isPending) {
    return <Loader title="Ověřuji možnost vyzkoušení za 1 Kč…" />;
  }
  if (checkTrial && trial.data?.eligible) {
    return <Navigate to={RETURN_TRIAL_PATH} replace />;
  }

  return <Outlet />;
};

export default AuthGate;
